import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut,
    Search,
    Mail,
    Phone,
    RefreshCw,
    CheckCircle2,
    Clock,
    Archive,
    ExternalLink,
} from "lucide-react";
import { AuthApi, AdminMessagesApi } from "../lib/adminApi";
import { PROFILE } from "../lib/profile";

const STATUS_LABEL = {
    pending: "Pendiente",
    contacted: "Contactado",
    closed: "Cerrado",
};

const STATUS_STYLES = {
    pending:
        "bg-warm-50 text-warm-600 border-warm-200",
    contacted:
        "bg-accentblue/10 text-accentblue border-accentblue/30",
    closed: "bg-navy/5 text-navy/60 border-navy/20",
};

const STATUS_ICON = {
    pending: Clock,
    contacted: CheckCircle2,
    closed: Archive,
};

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleString("es-MX", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        contacted: 0,
        closed: 0,
    });
    const [statusFilter, setStatusFilter] = useState("");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (q.trim()) params.q = q.trim();
            const [msgRes, statRes] = await Promise.all([
                AdminMessagesApi.list(params),
                AdminMessagesApi.stats(),
            ]);
            setMessages(msgRes.data);
            setStats(statRes.data);
        } catch (e) {
            // 401 handled by interceptor
            if (e?.response?.status !== 401) {
                toast.error("No se pudieron cargar los mensajes");
            }
        } finally {
            setLoading(false);
        }
    }, [statusFilter, q]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        // Verify token on mount
        AuthApi.me().catch(() => {
            navigate("/admin/login", { replace: true });
        });
    }, [navigate]);

    const handleLogout = () => {
        AuthApi.logout();
        navigate("/admin/login", { replace: true });
    };

    const updateStatus = async (id, next) => {
        try {
            await AdminMessagesApi.updateStatus(id, next);
            setMessages((list) =>
                list.map((m) => (m.id === id ? { ...m, status: next } : m)),
            );
            setStats((s) => {
                const prev = messages.find((m) => m.id === id)?.status || "pending";
                return {
                    ...s,
                    [prev]: Math.max(0, s[prev] - 1),
                    [next]: s[next] + 1,
                };
            });
            if (selected?.id === id) setSelected((m) => ({ ...m, status: next }));
            toast.success(`Marcado como ${STATUS_LABEL[next]}`);
        } catch (e) {
            toast.error("No se pudo actualizar el estado");
        }
    };

    const StatusBadge = ({ status }) => {
        const Icon = STATUS_ICON[status] || Clock;
        return (
            <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] border ${STATUS_STYLES[status]}`}
            >
                <Icon className="w-3 h-3" />
                {STATUS_LABEL[status]}
            </span>
        );
    };

    const stats_blocks = useMemo(
        () => [
            { key: "total", label: "Total", val: stats.total, color: "text-navy" },
            {
                key: "pending",
                label: "Pendientes",
                val: stats.pending,
                color: "text-warm-600",
            },
            {
                key: "contacted",
                label: "Contactados",
                val: stats.contacted,
                color: "text-accentblue",
            },
            {
                key: "closed",
                label: "Cerrados",
                val: stats.closed,
                color: "text-navy/50",
            },
        ],
        [stats],
    );

    return (
        <div
            className="min-h-screen bg-cream text-navy"
            data-testid="admin-dashboard"
        >
            {/* Header */}
            <header className="border-b border-navy/10 bg-cream/90 backdrop-blur-xl sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <a href="/" className="font-display text-xl text-navy">
                            Gilberto<span className="text-warm-600">.</span>Veliz
                        </a>
                        <span className="hidden md:inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.25em] bg-navy text-cream">
                            Admin
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={loadData}
                            className="hidden sm:inline-flex items-center gap-2 text-xs text-navy/70 hover:text-navy px-3 py-2"
                            data-testid="btn-refresh"
                        >
                            <RefreshCw
                                className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
                            />
                            Actualizar
                        </button>
                        <button
                            onClick={handleLogout}
                            className="inline-flex items-center gap-2 border border-navy/20 text-navy text-xs px-4 py-2 hover:bg-navy hover:text-cream transition-colors"
                            data-testid="btn-logout"
                        >
                            <LogOut className="w-3.5 h-3.5" />
                            Salir
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
                <div className="mb-8">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-navy/50">
                        Dashboard
                    </div>
                    <h1 className="mt-1 font-display text-4xl md:text-5xl text-navy">
                        Mensajes recibidos
                    </h1>
                </div>

                {/* Stats */}
                <div
                    className="grid grid-cols-2 md:grid-cols-4 border border-navy/15 bg-white"
                    data-testid="admin-stats"
                >
                    {stats_blocks.map((s, i) => (
                        <button
                            key={s.key}
                            onClick={() =>
                                setStatusFilter(s.key === "total" ? "" : s.key)
                            }
                            className={`text-left p-6 transition-colors ${
                                i > 0 ? "border-l border-navy/10" : ""
                            } ${
                                (s.key === "total" && !statusFilter) ||
                                statusFilter === s.key
                                    ? "bg-cream-200"
                                    : "hover:bg-cream-200/60"
                            }`}
                            data-testid={`stat-${s.key}`}
                        >
                            <div className="text-[10px] uppercase tracking-[0.2em] text-navy/50">
                                {s.label}
                            </div>
                            <div
                                className={`mt-2 font-display text-4xl ${s.color}`}
                            >
                                {s.val}
                            </div>
                        </button>
                    ))}
                </div>

                {/* Search */}
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Buscar por nombre, email, asunto…"
                            className="w-full bg-white border border-navy/15 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-navy"
                            data-testid="search-input"
                        />
                    </div>
                    <div
                        className="flex items-center gap-1 border border-navy/15 bg-white px-1"
                        data-testid="status-filter"
                    >
                        {[
                            { k: "", l: "Todos" },
                            { k: "pending", l: "Pendientes" },
                            { k: "contacted", l: "Contactados" },
                            { k: "closed", l: "Cerrados" },
                        ].map((o) => (
                            <button
                                key={o.k}
                                onClick={() => setStatusFilter(o.k)}
                                className={`px-3 py-2 text-xs tracking-wide transition-colors ${
                                    statusFilter === o.k
                                        ? "bg-navy text-cream"
                                        : "text-navy/60 hover:text-navy"
                                }`}
                                data-testid={`filter-${o.k || "all"}`}
                            >
                                {o.l}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Messages table */}
                <div
                    className="mt-8 border border-navy/15 bg-white overflow-hidden"
                    data-testid="messages-table-wrap"
                >
                    {loading ? (
                        <div className="p-16 text-center text-navy/50 text-sm">
                            Cargando…
                        </div>
                    ) : messages.length === 0 ? (
                        <div
                            className="p-16 text-center text-navy/50 text-sm"
                            data-testid="empty-state"
                        >
                            No hay mensajes que coincidan con tus filtros.
                        </div>
                    ) : (
                        <div className="divide-y divide-navy/10">
                            {messages.map((m) => (
                                <article
                                    key={m.id}
                                    className="p-5 md:p-6 hover:bg-cream-200/40 transition-colors cursor-pointer"
                                    onClick={() => setSelected(m)}
                                    data-testid={`message-row-${m.id}`}
                                >
                                    <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-6">
                                        <div className="md:w-48 flex-shrink-0">
                                            <div className="text-xs text-navy/50">
                                                {formatDate(m.created_at)}
                                            </div>
                                            <div className="mt-1">
                                                <StatusBadge status={m.status} />
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <div className="font-display text-xl text-navy leading-tight">
                                                        {m.name}
                                                    </div>
                                                    <div className="text-xs text-navy/60 mt-0.5">
                                                        {m.email}
                                                        {m.phone ? ` · ${m.phone}` : ""}
                                                    </div>
                                                </div>
                                            </div>
                                            {m.subject && (
                                                <div className="mt-2 text-sm text-navy/80 font-medium">
                                                    {m.subject}
                                                </div>
                                            )}
                                            <p className="mt-1 text-sm text-navy/70 line-clamp-2">
                                                {m.message}
                                            </p>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            {/* Detail drawer */}
            {selected && (
                <div
                    className="fixed inset-0 z-50 flex"
                    data-testid="message-drawer"
                >
                    <div
                        className="flex-1 bg-navy/40 backdrop-blur-sm"
                        onClick={() => setSelected(null)}
                    />
                    <div className="w-full max-w-xl bg-white border-l border-navy/15 overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-navy/10 px-6 py-4 flex items-center justify-between">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-navy/50">
                                Mensaje
                            </div>
                            <button
                                onClick={() => setSelected(null)}
                                className="text-navy/60 hover:text-navy text-sm"
                                data-testid="close-drawer"
                            >
                                Cerrar ✕
                            </button>
                        </div>
                        <div className="p-6 md:p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <StatusBadge status={selected.status} />
                                <span className="text-xs text-navy/50">
                                    {formatDate(selected.created_at)}
                                </span>
                            </div>
                            <h2 className="font-display text-3xl text-navy">
                                {selected.name}
                            </h2>

                            <div className="mt-4 space-y-2 text-sm">
                                <div className="flex items-center gap-2 text-navy/80">
                                    <Mail className="w-4 h-4 text-accentblue" />
                                    <a
                                        href={`mailto:${selected.email}?subject=Re:%20${encodeURIComponent(selected.subject || "Tu mensaje")}`}
                                        className="hover:underline"
                                        data-testid="reply-email"
                                    >
                                        {selected.email}
                                    </a>
                                </div>
                                {selected.phone && (
                                    <div className="flex items-center gap-2 text-navy/80">
                                        <Phone className="w-4 h-4 text-accentblue" />
                                        <a
                                            href={PROFILE.whatsapp(
                                                `Hola ${selected.name}, respondo tu mensaje del sitio.`,
                                            )}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="hover:underline"
                                            data-testid="reply-whatsapp"
                                        >
                                            {selected.phone}
                                        </a>
                                    </div>
                                )}
                            </div>

                            {selected.subject && (
                                <div className="mt-6">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-navy/50 mb-1">
                                        Asunto
                                    </div>
                                    <div className="font-display text-lg text-navy">
                                        {selected.subject}
                                    </div>
                                </div>
                            )}

                            <div className="mt-6">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-navy/50 mb-1">
                                    Mensaje
                                </div>
                                <div className="p-4 bg-cream border-l-4 border-warm whitespace-pre-wrap text-sm text-navy/90 leading-relaxed">
                                    {selected.message}
                                </div>
                            </div>

                            <div className="mt-8">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-navy/50 mb-3">
                                    Cambiar estado
                                </div>
                                <div className="grid grid-cols-3 gap-2" data-testid="status-actions">
                                    {["pending", "contacted", "closed"].map((s) => {
                                        const Icon = STATUS_ICON[s];
                                        const active = selected.status === s;
                                        return (
                                            <button
                                                key={s}
                                                onClick={() => updateStatus(selected.id, s)}
                                                disabled={active}
                                                className={`flex items-center justify-center gap-2 py-3 text-xs tracking-wide border transition-colors ${
                                                    active
                                                        ? "bg-navy text-cream border-navy"
                                                        : "border-navy/20 text-navy hover:bg-navy hover:text-cream"
                                                }`}
                                                data-testid={`status-${s}`}
                                            >
                                                <Icon className="w-3.5 h-3.5" />
                                                {STATUS_LABEL[s]}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="mt-8 pt-6 border-t border-navy/10 flex gap-3">
                                <a
                                    href={`mailto:${selected.email}?subject=Re:%20${encodeURIComponent(selected.subject || "Tu mensaje")}`}
                                    className="flex-1 inline-flex items-center justify-center gap-2 bg-navy text-cream px-4 py-3 text-sm hover:bg-warm-600 transition-colors"
                                    data-testid="cta-email-reply"
                                >
                                    <Mail className="w-4 h-4" />
                                    Responder por email
                                </a>
                                {selected.phone && (
                                    <a
                                        href={PROFILE.whatsapp(
                                            `Hola ${selected.name}, respondo tu mensaje del sitio.`,
                                        )}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center justify-center gap-2 border border-navy/20 text-navy px-4 py-3 text-sm hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-colors"
                                        data-testid="cta-whatsapp-reply"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        WhatsApp
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
